// @givmo/design-system — Badge (SaaS layer).
//
// A small status chip for tables/cards. Subtle-tint recipe (NOT a consumer pill):
//   - `rounded-md` (6px) corners, w500 label, `tracking-normal` (the global -0.3px tracking hurts
//     11–12px text);
//   - tinted tones (brand/success/warning/danger/info): `{tone}-50` bg + `{tone}-700` text, NO
//     border;
//   - `neutral`: `neutral-100` bg + `neutral-700` text WITH a hairline `neutral-200` border (the
//     only tone that carries a border, so a status-less label still reads as a chip);
//   - optional 6px `{tone}-500` leading dot.
// Used for row statuses (Active/Pending/Failed), counts, roles, and labels.
//
// `wrap` is the exception to all of that, and it exists because one caller does not hand this
// component a status word: `PageHeader`'s organization-name chip carries a charity's LEGAL name,
// routinely 50–80 characters. The base recipe is `whitespace-nowrap`, so such a label is a single
// unbreakable box whose min-content width is the whole name — in the header's stacked phone layout
// that widened the DOCUMENT and scrolled three live pages sideways at 375px (build_and_ci.md
// § nowrap badge; D-CP-56 rules that the name wraps rather than truncating). `wrap` relaxes exactly
// the three properties that causes: the label wraps, the chip may shrink below its own min-content
// inside a flex row, and its height follows its content. That last part is not optional — `size`
// pins a FIXED `h-*`, so a wrapped label would otherwise paint outside its own chip's background.
//
// ⚠ `wrap-anywhere`, NOT `break-words` — the difference is the whole cure for a name with no spaces.
// This chip is `inline-flex`, so its label is an ANONYMOUS FLEX ITEM: `min-w-0` sits on the chip and
// does not reach that item, and `overflow-wrap: break-word` (`break-words`) does not reduce a
// token's min-content contribution. So a single unbroken run wider than the lane kept its full
// width, painted outside the chip and grew the document all over again — `break-words` was pinned
// here and inert. `overflow-wrap: anywhere` (`wrap-anywhere`) is the one that DOES reduce it, and it
// subsumes `break-word`, so it replaces rather than joins it. Measured at 375: a 90-character
// unbroken token scrolled the document 781px under `break-words`, 0px under `wrap-anywhere`, with
// every realistic name's chip box identical to the pixel at 375 / 1024 / 1440.
//
// The paired `min-h-*`/`py-*` keep a SINGLE-line wrap chip at exactly the height a non-wrapping one
// has always been, which is what leaves every desktop header pixel-identical. The arithmetic is
// `line box + 2·py + 2·border = h-<size>` under `box-sizing: border-box`, and the border term is
// the trap: `neutral` is the one tone that carries a 1px hairline, so md is 18 + 2·2 + 2 = 24, not
// the 18 + 2·3 that reads right on paper (`py-3` measured 26px in the harness — caught only because
// the chip was measured rather than reasoned about). sm's 16.5px line box lands under `h-20` with no
// padding at all, so `min-h-20` alone does the clamping there. Both belong here beside `size`'s
// heights, not in a caller's class string: move them together or the chip silently changes height.
//
// None of this is visible to jsdom, which lays nothing out — a unit pin can assert that a class is
// PRESENT, never that it does anything, which is exactly how an inert `break-words` sat here behind
// a green pin. Re-measure in a real browser at 375 before changing any of it.
import { forwardRef } from 'react';
import type { HTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/utils';
import { testIdProp, type TestIdProps } from '../lib/testid';

const badgeVariants = cva(
  [
    'inline-flex items-center gap-6 whitespace-nowrap rounded-md',
    'font-sans font-w500 tracking-normal',
  ],
  {
    variants: {
      tone: {
        neutral: 'border border-neutral-200 bg-neutral-100 text-neutral-700',
        brand: 'bg-brand-50 text-brand-700',
        success: 'bg-success-50 text-success-700',
        warning: 'bg-warning-50 text-warning-700',
        danger: 'bg-danger-50 text-danger-700',
        info: 'bg-info-50 text-info-700',
      },
      size: {
        sm: 'h-20 px-6 text-s11',
        md: 'h-24 px-8 text-s12',
      },
      // Declared AFTER `size` so its `h-auto` wins the height conflict through `cn`/tailwind-merge.
      wrap: {
        true: 'h-auto min-w-0 whitespace-normal wrap-anywhere',
        false: '',
      },
    },
    // The wrap chip's vertical rhythm is a function of its size, so the two are paired here rather
    // than duplicated into either variant. See the height arithmetic in the header.
    compoundVariants: [
      { size: 'sm', wrap: true, class: 'min-h-20' },
      { size: 'md', wrap: true, class: 'min-h-24 py-2' },
    ],
    defaultVariants: {
      tone: 'neutral',
      size: 'sm',
      wrap: false,
    },
  },
);

const DOT_COLOR: Record<NonNullable<VariantProps<typeof badgeVariants>['tone']>, string> = {
  neutral: 'bg-neutral-400',
  brand: 'bg-brand-500',
  success: 'bg-success-500',
  warning: 'bg-warning-500',
  danger: 'bg-danger-500',
  info: 'bg-info-500',
};

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants>, TestIdProps {
  /** Show a leading status dot in the tone color. */
  dot?: boolean;
}

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(function Badge(
  { className, tone, size, wrap, dot = false, testId, children, ...props },
  ref,
) {
  const resolvedTone = tone ?? 'neutral';
  return (
    <span
      ref={ref}
      className={cn(badgeVariants({ tone, size, wrap }), className)}
      {...testIdProp(testId)}
      {...props}
    >
      {dot && <span className={cn('size-6 shrink-0 rounded-full', DOT_COLOR[resolvedTone])} />}
      {children}
    </span>
  );
});

export { badgeVariants };
